import { postFromRecord } from './social.ts';
import type { ShopHours } from '@guildite/domain/hours';
import { readPublicShopLocation } from '@guildite/domain/location';
import { listingFromProduct } from '@guildite/domain/listings';

import type { DiscoverPort, PersonCard, ReportsPort, SavedPort, ShopCard } from './ports.ts';
import type { WorkerClient } from './worker.ts';

export type QueryFn = (collection: string, options: {
  filters?: [string, '==' | '>=' | '<', unknown][];
  orderBy?: [string, 'asc' | 'desc'];
  limit?: number;
}) => Promise<{ id: string; data: Record<string, unknown> }[]>;

const words = (value: string) => value.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').split(/\s+/).filter(Boolean);

// Matches every word of the search somewhere in the text.
export function matches(query: string, ...fields: unknown[]): boolean {
  const wanted = words(query);
  if (!wanted.length) return true;
  const haystack = words(fields.map(field => String(field ?? '')).join(' ')).join(' ');
  return wanted.every(word => haystack.includes(word));
}

function safeMedia(url: unknown): string | null {
  const value = String(url || '');
  return value.startsWith('https://') || value.startsWith('http://127.0.0.1:') ? value : null;
}

export function createDiscover(query: QueryFn): DiscoverPort {
  return {
    async listings({ query: text = '', category = '', limit = 60 } = {}) {
      const rows = await query('products', { filters: [['status', '==', 'Active']], orderBy: ['updatedAt', 'desc'], limit: 200 });
      return rows.map(row => listingFromProduct(row.id, row.data))
        .filter(listing => listing.photos.length && (!category || listing.category.toLowerCase() === category.toLowerCase()))
        .filter(listing => matches(text, listing.name, listing.category, listing.shopName, listing.description))
        .slice(0, limit);
    },
    async listingsInPlace(key) {
      const rows = await query('products', { filters: [['placeKey', '==', key], ['status', '==', 'Active']], limit: 60 });
      return rows.map(row => listingFromProduct(row.id, row.data)).filter(listing => listing.photos.length)
        .sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? ''));
    },
    async shops(text = '') {
      const rows = await query('shops', { filters: [['status', '==', 'active']], orderBy: ['updatedAt', 'desc'], limit: 200 });
      return rows.map(({ id, data }): ShopCard => {
        const location = (data.location ?? {}) as Record<string, unknown>;
        const publicLocation = readPublicShopLocation(data.location);
        return { shopId: id, handle: typeof data.handle === 'string' ? data.handle : null, name: String(data.name || 'Shop'), photoUrl: safeMedia(data.logoUrl), category: String(data.category || ''), area: publicLocation.area || String(location.area || ''), location: publicLocation, hours: data.hours && typeof data.hours === 'object' ? data.hours as ShopHours : null };
      }).filter(shop => matches(text, shop.name, shop.handle, shop.category, shop.area)).slice(0, 50);
    },
    async people(text) {
      const prefix = text.trim().replace(/^@/, '').toLowerCase();
      if (prefix.length < 2) return [];
      const rows = await query('profiles', { filters: [['handle', '>=', prefix], ['handle', '<', `${prefix}`]], orderBy: ['handle', 'asc'], limit: 20 });
      return rows.map(({ id, data }): PersonCard => ({ uid: id, handle: String(data.handle || ''), name: String(data.name || 'Guildite member'), photoUrl: safeMedia(data.photoUrl) }));
    },
  };
}

export function createSaved({ worker, query, readDocument, currentUid }: {
  worker: WorkerClient;
  query: (collection: string) => Promise<{ id: string; data: Record<string, unknown> }[]>;
  readDocument: (collection: string, id: string) => Promise<Record<string, unknown> | null>;
  currentUid: () => string | null;
}): SavedPort {
  async function ids(type: 'listing' | 'post' = 'listing'): Promise<string[]> {
    const uid = currentUid();
    if (!uid) return [];
    const rows = await query(`saves/${uid}/items`);
    return rows.filter(row => row.data.targetType === type).map(row => String(row.data.targetId));
  }
  return {
    async listings() {
      const products = await Promise.all((await ids()).map(async id => ({ id, product: await readDocument('products', id) })));
      return products.filter(item => item.product).map(item => listingFromProduct(item.id, item.product!));
    },
    savedIds: async () => new Set(await ids()),
    set: async (listingId, saved) => { await worker.call('set_saved', { targetType: 'listing', targetId: listingId, saved }); },
    async posts() {
      const posts = await Promise.all((await ids('post')).map(async id => ({ id, post: await readDocument('posts', id) })));
      return posts.filter(item => item.post?.status === 'active').map(item => postFromRecord(item.id, item.post!));
    },
    savedPostIds: async () => new Set(await ids('post')),
    setPost: async (postId, saved) => { await worker.call('set_saved', { targetType: 'post', targetId: postId, saved }); },
  };
}

export function createReports(worker: WorkerClient): ReportsPort {
  return {
    report: input => worker.call('create_report', {
      ...input,
      submissionKey: `${input.targetType}-${input.targetId}-${Date.now()}`,
      pagePath: typeof location === 'undefined' ? '/' : location.pathname,
    }),
  };
}
